// src/App.tsx
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';

// Pages
import Index from "./pages/Index";
import Pohistvo from "./pages/Pohistvo";
import Umivalniki from "./pages/Umivalniki";
import DrobnoPohistvo from "./pages/DrobnoPohistvo";
import Svetila from "./pages/Svetila";
import Ogledala from "./pages/Ogledala";
import Dodatki from "./pages/Dodatki";
import Kontakt from "./pages/Kontakt";
import NotFound from "./pages/NotFound";

// Legal Pages
import PogojiPoslovanja from "./pages/legal/PogojiPoslovanja";
import Zasebnost from "./pages/legal/Zasebnost";
import Piskotki from "./pages/legal/Piskotki";
import GDPR from "./pages/legal/GDPR";

// Umivalniki Subcategories
import StekleniUmivalniki from "./pages/umivalniki/StekleniUmivalniki";
import GlineniUmivalniki from "./pages/umivalniki/GlineniUmivalniki";
import BetonskiUmivalniki from "./pages/umivalniki/BetonskiUmivalniki";
import ResinaUmivalniki from "./pages/umivalniki/ResinaUmivalniki";
import MarmorniUmivalniki from "./pages/umivalniki/MarmorniUmivalniki";
import LeseniUmivalniki from "./pages/umivalniki/LeseniUmivalniki";
import MetalUmivalniki from "./pages/umivalniki/MetalUmivalniki";

// Pohištvo Subcategories
import Elegance from "./pages/pohistvo/Elegance";
import DobroPocutje from "./pages/pohistvo/DobroPocutje";
import Harmonija from "./pages/pohistvo/Harmonija";
import Natur from "./pages/pohistvo/Natur";

// Ogledala Subcategories
import Freestanding from "./pages/ogledala/Freestanding";
import Vanity from "./pages/ogledala/Vanity";
import Eclisse from "./pages/ogledala/Eclisse";
import TokyoOval from "./pages/ogledala/TokyoOval";
import TokyoRound from "./pages/ogledala/TokyoRound";
import TokyoLente from "./pages/ogledala/TokyoLente";
import Backlit from "./pages/ogledala/Backlit";
import Belt from "./pages/ogledala/Belt";
import Cronografo from "./pages/ogledala/Cronografo";
import TicTac from "./pages/ogledala/TicTac";
import OrdinaRound from "./pages/ogledala/OrdinaRound";
import ReflexShelf from "./pages/ogledala/ReflexShelf";
import LeCuir from "./pages/ogledala/LeCuir";
import Irene from "./pages/ogledala/Irene";
import Feel from "./pages/ogledala/Feel";

// Svetila Subcategories
import IndexSupportLights from "./pages/svetila/IndexSupportLights";
import TuraregCeilingLights from "./pages/svetila/TuraregCeilingLights";
import IndexCeilingLights from "./pages/svetila/IndexCeilingLights";

// Drobno Pohistvo Subcategories
import MuvetPanca from "./pages/drobno-pohistvo/MuvetPanca";
import TuaregVases from "./pages/drobno-pohistvo/TuaregVases";
import VeloceStool from "./pages/drobno-pohistvo/VeloceStool";
import VanityStool from "./pages/drobno-pohistvo/VanityStool";
import EdmundSgabello from "./pages/drobno-pohistvo/EdmundSgabello";
import Basket from "./pages/drobno-pohistvo/Basket";
import EdmundTable from "./pages/drobno-pohistvo/EdmundTable";
import MarbleTray from "./pages/drobno-pohistvo/MarbleTray";
import MilordPortaAsciugamani from "./pages/drobno-pohistvo/MilordPortaAsciugamani";
import PiegamiStool from "./pages/drobno-pohistvo/PiegamiStool";
import GhostStool from "./pages/drobno-pohistvo/GhostStool";
import BiboScala from "./pages/drobno-pohistvo/BiboScala";
import UilliStair from "./pages/drobno-pohistvo/UilliStair";
import Compasso from "./pages/drobno-pohistvo/Compasso";
import SarongStool from "./pages/drobno-pohistvo/SarongStool";
import OrdinaStool from "./pages/drobno-pohistvo/OrdinaStool";
import TanchoStool from "./pages/drobno-pohistvo/TanchoStool";
import KoiStool from "./pages/drobno-pohistvo/KoiStool";
import TrickyStool from "./pages/drobno-pohistvo/TrickyStool";
import TwisterStool from "./pages/drobno-pohistvo/TwisterStool";
import Portami from "./pages/drobno-pohistvo/Portami";
import SnakeTree from "./pages/drobno-pohistvo/SnakeTree";
import Tree from "./pages/drobno-pohistvo/Tree";
import Comoda from "./pages/drobno-pohistvo/Comoda";
import Arya from "./pages/drobno-pohistvo/Arya";
import ZenStool from "./pages/drobno-pohistvo/ZenStool";
import Adirondack from "./pages/drobno-pohistvo/Adirondack";
import InvillaStool from "./pages/drobno-pohistvo/InvillaStool";

// Dodatki Subcategories
import SmolnatiDodatki from "./pages/dodatki/SmolnatiDodatki";
import StekleniDodatki from "./pages/dodatki/StekleniDodatki";
import KeramicniDodatki from "./pages/dodatki/KeramicniDodatki";
import KamnitiDodatki from "./pages/dodatki/KamnitiDodatki";
import LeseniDodatki from "./pages/dodatki/LeseniDodatki";
import KovinskiDodatki from "./pages/dodatki/KovinskiDodatki";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/pohistvo" element={<Pohistvo />} />
            
            {/* Pohištvo Subcategory Routes */}
            <Route path="/pohistvo/kolekcija-eleganca" element={<Elegance />} />
            <Route path="/pohistvo/kolekcija-dobro-pocutje" element={<DobroPocutje />} />
            <Route path="/pohistvo/kolekcija-harmonija" element={<Harmonija />} />
            <Route path="/pohistvo/kolekcija-natur" element={<Natur />} />
            
            <Route path="/umivalniki" element={<Umivalniki />} />
            
            {/* Umivalniki Subcategory Routes */}
            <Route path="/umivalniki/stekleni" element={<StekleniUmivalniki />} />
            <Route path="/umivalniki/glineni" element={<GlineniUmivalniki />} />
            <Route path="/umivalniki/betonski" element={<BetonskiUmivalniki />} />
            <Route path="/umivalniki/resina" element={<ResinaUmivalniki />} />
            <Route path="/umivalniki/marmorni" element={<MarmorniUmivalniki />} />
            <Route path="/umivalniki/leseni" element={<LeseniUmivalniki />} />
            <Route path="/umivalniki/metal" element={<MetalUmivalniki />} />
            
            <Route path="/drobno-pohistvo" element={<DrobnoPohistvo />} />
            <Route path="/svetila" element={<Svetila />} />
            
            {/* Svetila Subcategory Routes */}
            <Route path="/svetila/index-support" element={<IndexSupportLights />} />
            <Route path="/svetila/turareg-ceiling" element={<TuraregCeilingLights />} />
            <Route path="/svetila/index-ceiling" element={<IndexCeilingLights />} />
            
            <Route path="/ogledala" element={<Ogledala />} />
            
            {/* Ogledala Subcategory Routes */}
            <Route path="/ogledala/freestanding" element={<Freestanding />} />
            <Route path="/ogledala/vanity" element={<Vanity />} />
            <Route path="/ogledala/eclisse" element={<Eclisse />} />
            <Route path="/ogledala/tokyo-oval" element={<TokyoOval />} />
            <Route path="/ogledala/tokyo-round" element={<TokyoRound />} />
            <Route path="/ogledala/tokyo-lente" element={<TokyoLente />} />
            <Route path="/ogledala/backlit" element={<Backlit />} />
            <Route path="/ogledala/belt" element={<Belt />} />
            <Route path="/ogledala/cronografo" element={<Cronografo />} />
            <Route path="/ogledala/tictac" element={<TicTac />} />
            <Route path="/ogledala/ordina-round" element={<OrdinaRound />} />
            <Route path="/ogledala/reflex-shelf" element={<ReflexShelf />} />
            <Route path="/ogledala/le-cuir" element={<LeCuir />} />
            <Route path="/ogledala/irene" element={<Irene />} />
            <Route path="/ogledala/feel" element={<Feel />} />
            
            <Route path="/dodatki" element={<Dodatki />} />
            
            {/* Dodatki Subcategory Routes */}
            <Route path="/dodatki/smolnati" element={<SmolnatiDodatki />} />
            <Route path="/dodatki/stekleni" element={<StekleniDodatki />} />
            <Route path="/dodatki/keramicni" element={<KeramicniDodatki />} />
            <Route path="/dodatki/kamniti" element={<KamnitiDodatki />} />
            <Route path="/dodatki/leseni" element={<LeseniDodatki />} />
            <Route path="/dodatki/kovinski" element={<KovinskiDodatki />} />
            
            <Route path="/kontakt" element={<Kontakt />} />
            
            {/* Legal Pages Routes */}
            <Route path="/pogoji-poslovanja" element={<PogojiPoslovanja />} />
            <Route path="/zasebnost" element={<Zasebnost />} />
            <Route path="/piskotki" element={<Piskotki />} />
            <Route path="/gdpr" element={<GDPR />} />
            
            {/* Drobno Pohistvo Subcategory Routes */}
            <Route path="/drobno-pohistvo/muvet-panca" element={<MuvetPanca />} />
            <Route path="/drobno-pohistvo/tuareg-vases" element={<TuaregVases />} />
            <Route path="/drobno-pohistvo/veloce-stool" element={<VeloceStool />} />
            <Route path="/drobno-pohistvo/vanity-stool" element={<VanityStool />} />
            <Route path="/drobno-pohistvo/edmund-sgabello" element={<EdmundSgabello />} />
            <Route path="/drobno-pohistvo/basket" element={<Basket />} />
            <Route path="/drobno-pohistvo/edmund-table" element={<EdmundTable />} />
            <Route path="/drobno-pohistvo/marble-tray" element={<MarbleTray />} />
            <Route path="/drobno-pohistvo/milord-porta-asciugamani" element={<MilordPortaAsciugamani />} />
            <Route path="/drobno-pohistvo/piegami-stool" element={<PiegamiStool />} />
            <Route path="/drobno-pohistvo/ghost-stool" element={<GhostStool />} />
            <Route path="/drobno-pohistvo/bibo-scala" element={<BiboScala />} />
            <Route path="/drobno-pohistvo/uilli-stair" element={<UilliStair />} />
            <Route path="/drobno-pohistvo/compasso" element={<Compasso />} />
            <Route path="/drobno-pohistvo/sarong-stool" element={<SarongStool />} />
            <Route path="/drobno-pohistvo/ordina-stool" element={<OrdinaStool />} />
            <Route path="/drobno-pohistvo/tancho-stool" element={<TanchoStool />} />
            <Route path="/drobno-pohistvo/koi-stool" element={<KoiStool />} />
            <Route path="/drobno-pohistvo/tricky-stool" element={<TrickyStool />} />
            <Route path="/drobno-pohistvo/twister-stool" element={<TwisterStool />} />
            <Route path="/drobno-pohistvo/portami" element={<Portami />} />
            <Route path="/drobno-pohistvo/snake-tree" element={<SnakeTree />} />
            <Route path="/drobno-pohistvo/tree" element={<Tree />} />
            <Route path="/drobno-pohistvo/comoda" element={<Comoda />} />
            <Route path="/drobno-pohistvo/arya" element={<Arya />} />
            <Route path="/drobno-pohistvo/zen-stool" element={<ZenStool />} />
            <Route path="/drobno-pohistvo/adirondack" element={<Adirondack />} />
            <Route path="/drobno-pohistvo/invilla-stool" element={<InvillaStool />} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;